import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Card, Spinner, Alert, Icon, StatTile } from '../../../core/ui';
import { useTranslation } from '../../../core/context/TranslationContext';

import { getDashboardStats } from '../api/reports';
import styles from './ReceiptsOverviewWidget.module.css';

/**
 * Receipt activity only. Spending totals live in the budget widget and the
 * spending reports, which count a receipt and its card charge once.
 */
export default function ReceiptsOverviewWidget() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalReceipts: 0,
    recentStores: [],
    receiptsUploadedLast7Days: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getDashboardStats();
        if (!cancelled) {
          setStats((prev) => ({ ...prev, ...data }));
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        if (!cancelled) setError(t('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('module.receipts')}</Card.Title>
        {!loading && !error && (
          <Link to="/receipts" className={styles.viewAll}>
            {t('viewAll')} →
          </Link>
        )}
      </Card.Header>

      <Card.Body>
        {loading && (
          <div className={styles.centered}>
            <Spinner label={t('loading')} />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && (
          <>
            <div className={styles.statsGrid}>
              <StatTile label={t('totalReceipts')} value={stats.totalReceipts} />
              <StatTile label={t('receiptsUploadedLast7Days')} value={stats.receiptsUploadedLast7Days} />
            </div>

            <div className={styles.actions}>
              <Link to="/upload" className={styles.actionLink}>
                <Icon name="fa-upload" size="sm" />
                {t('uploadReceipt')}
              </Link>
              <Link to="/receipts" className={styles.actionLink}>
                <Icon name="fa-history" size="sm" />
                {t('receiptHistory')}
              </Link>
              <Link to="/reports" className={styles.actionLink}>
                <Icon name="fa-chart-line" size="sm" />
                {t('reports')}
              </Link>
            </div>

            {(stats.recentStores || []).length > 0 && (
              <>
                <div className={styles.chartTitle}>{t('recentStores')}</div>
                <div className={styles.chips}>
                  {stats.recentStores.map((store, idx) => (
                    <span key={`${idx}-${store}`} className={styles.chip}>
                      <Icon name="fa-store" size="sm" />
                      {store}
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
